import { useMemo } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import type { ProductItem } from '../types/myStore.types';
import IcCopy from '@/assets/icons/ic-copy.svg';
import IcEdit from '@/assets/icons/ic-edit-01.svg';
import IcHidden from '@/assets/icons/ic-hidden.svg';
import IcDuplicate from '@/assets/icons/ic-duplicate.svg';
import IcPointer from '@/assets/icons/ic-cursor.svg';
import IcLineChart from '@/assets/icons/ic-line-chart.svg';
import IcTrash from '@/assets/icons/ic-trash.svg';

export interface ProductActionsMenuProps {
    item: ProductItem;
    onCopyUrl?: () => void;
    onEdit?: () => void;
    onUnpublish?: () => void;
    onDuplicate?: () => void;
    onMoveToLanding?: () => void;
    onMoveToStore?: () => void;
    onAnalytics?: () => void;
    onDelete?: () => void;
    onAffiliateAnalytics?: () => void;
}

export function ProductActionsMenu({
    item,
    onCopyUrl,
    onEdit,
    onUnpublish,
    onDuplicate,
    onMoveToLanding,
    onMoveToStore,
    onAnalytics,
    onDelete,
    onAffiliateAnalytics,
}: ProductActionsMenuProps) {
    const menuOptions = useMemo((): DropdownMenuOption[] => {
        const options: DropdownMenuOption[] = [];

        if (item.location === 'store' || (item.location === 'landing' && item.page_type === 'lead-magnet')) {
            options.push({
                label: 'Copy URL',
                value: 'copy-url',
                icon: IcCopy,
            });
        }

        if (item.location === 'store' || (item.location === 'landing' && item.page_type === 'lead-magnet')) {
            options.push({
                label: 'Edit Product',
                value: 'edit',
                icon: IcEdit,
            });
        }

        if (item.status !== 1) {
            options.push({
                label: item.location === 'store' ? 'Unpublish' : 'Hide Page',
                value: 'unpublish',
                icon: IcHidden,
            });
        }

        if (item.page_type !== 'community') {
            options.push({
                label: item.location === 'store' ? 'Duplicate Product' : 'Duplicate Page',
                value: 'duplicate',
                icon: IcDuplicate,
            });
        }

        if (item.location === 'store') {
            options.push({
                label: 'Make Landing Page',
                value: 'move-to-landing',
                icon: IcPointer,
            });
        } else if (item.location === 'landing') {
            options.push({
                label: 'Move to Store',
                value: 'move-to-store',
                icon: IcPointer,
            });
        }

        if (item.page_type === 'course') {
            options.push({
                label: 'Analytics',
                value: 'analytics',
                icon: IcLineChart,
            });
        }

        if (options.length > 0) {
            options.push({
                label: '',
                value: 'divider-1',
                type: 'divider',
            });
        }

        options.push({
            label: item.location === 'store' ? 'Delete Product' : 'Delete Page',
            value: 'delete',
            icon: IcTrash,
            type: 'destructive',
        });

        return options;
    }, [item]);

    const handleSelect = (value: DropdownValue) => {
        switch (value.value) {
            case 'copy-url':
                onCopyUrl?.();
                break;
            case 'edit':
                onEdit?.();
                break;
            case 'unpublish':
                onUnpublish?.();
                break;
            case 'duplicate':
                onDuplicate?.();
                break;
            case 'move-to-landing':
                onMoveToLanding?.();
                break;
            case 'move-to-store':
                onMoveToStore?.();
                break;
            case 'analytics':
                onAnalytics?.();
                break;
            case 'delete':
                onDelete?.();
                break;
            case 'affiliate-analytics':
                onAffiliateAnalytics?.();
                break;
        }
    };

    return (
        <DropdownProvider dropdownId={`product-menu-${item.id}`} dropdownName={`product-menu-${item.id}`}>
            <StanDropdownMenu
                id={`product-menu-${item.id}`}
                name={`product-menu-${item.id}`}
                options={menuOptions}
                placement='bottom-end'
                onSelect={handleSelect}
                currentValue={null}
                minWidth={200}
            >
                <FiMoreVertical className='text-gray hover:text-dark size-5 cursor-pointer' />
            </StanDropdownMenu>
        </DropdownProvider>
    );
}
